"use client"

import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { ChevronDown } from "lucide-react"
import { useState, useCallback } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { buildCategoryTree } from "@lib/data/category-tree"

type FullCategorySidebarProps = {
  categories: HttpTypes.StoreProductCategory[]
  sortBy: SortOptions
  sourceStore?: string
}

const sortOptions: { value: SortOptions; label: string }[] = [
  { value: "created_at", label: "Latest Arrivals" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "title_asc", label: "Name A-Z" },
]

const CategoryGroup = ({ category }: { category: HttpTypes.StoreProductCategory }) => {
  const isVirtual = category.id.startsWith("virtual-")
  const defaultExpanded = (category.metadata as Record<string, unknown>)?.defaultExpanded !== false
  const [expanded, setExpanded] = useState(defaultExpanded)
  const children = (category.category_children || []) as HttpTypes.StoreProductCategory[]

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between">
        {isVirtual ? (
          <span className="text-sm font-semibold text-gray-900 py-1">
            {category.name}
          </span>
        ) : (
          <LocalizedClientLink
            href={`/categories/${category.handle}`}
            className="text-sm font-semibold text-gray-900 hover:text-[var(--primary)] py-1 transition-colors"
          >
            {category.name}
          </LocalizedClientLink>
        )}
        {children.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <ChevronDown
              size={14}
              className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>
      {expanded && children.length > 0 && (
        <div className="flex flex-col gap-0.5 mt-1">
          {children.map((child) => {
            const isChildVirtual = child.id.startsWith("virtual-")
            const grandchildren = (child.category_children || []) as HttpTypes.StoreProductCategory[]
            return (
              <div key={child.id}>
                {isChildVirtual ? (
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider py-1 pl-4 block">
                    {child.name}
                  </span>
                ) : (
                  <LocalizedClientLink
                    href={`/categories/${child.handle}`}
                    className="text-sm text-gray-600 hover:text-gray-900 py-1 pl-4 block rounded hover:bg-gray-50 transition-colors"
                  >
                    {child.name}
                  </LocalizedClientLink>
                )}
                {grandchildren.length > 0 && (
                  <div className="flex flex-col">
                    {grandchildren.map((gc) => (
                      <LocalizedClientLink
                        key={gc.id}
                        href={`/categories/${gc.handle}`}
                        className="text-xs text-gray-500 hover:text-gray-700 py-1 pl-8 block rounded hover:bg-gray-50 transition-colors"
                      >
                        {gc.name}
                      </LocalizedClientLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const SortBySection = ({ sortBy }: { sortBy: SortOptions }) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const setSort = useCallback(
    (value: SortOptions) => {
      const params = new URLSearchParams(searchParams)
      params.set("sortBy", value)
      router.push(`${pathname}?${params.toString()}`)
    },
    [router, pathname, searchParams]
  )

  return (
    <div className="flex flex-col">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Sort By
      </h3>
      <div className="flex flex-col gap-1">
        {sortOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setSort(option.value)}
            className={`text-sm text-left py-1.5 px-2 rounded transition-colors ${
              sortBy === option.value
                ? "bg-gray-100 text-gray-900 font-medium"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const FullCategorySidebar = ({ categories, sortBy, sourceStore }: FullCategorySidebarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false)

  const tree = buildCategoryTree(categories, sourceStore || "")
  const rootCategories = tree.filter(c => !c.parent_category)

  const categoryContent = rootCategories.length > 0 ? (
    <div className="flex flex-col">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Categories
      </h3>
      {rootCategories.map((category) => (
        <CategoryGroup key={category.id} category={category} />
      ))}
    </div>
  ) : null

  return (
    <>
      {/* Desktop */}
      <div className="hidden small:block w-[220px] shrink-0 pr-6 py-4">
        <SortBySection sortBy={sortBy} />
        {categoryContent && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            {categoryContent}
          </div>
        )}
      </div>
      {/* Mobile: expandable */}
      <div className="small:hidden mb-4">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="flex items-center justify-between w-full py-3 px-4 bg-gray-50 rounded-lg text-sm font-medium text-gray-700"
        >
          <span>Filters & Sort</span>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${mobileOpen ? "rotate-180" : ""}`}
          />
        </button>
        {mobileOpen && (
          <div className="mt-2 px-2 space-y-4">
            <SortBySection sortBy={sortBy} />
            {categoryContent && (
              <div className="pt-4 border-t border-gray-200">
                {categoryContent}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default FullCategorySidebar
